import { KnowledgeItemStatus } from "client/Api/KnowledgeItemManagement/knowledgeItemManagement";
import { TagViewModel } from "client/Api/Tags/tags";
import { SortDirection } from "client/Api/common";
import { environmentHttpClient } from "client/Api/httpClient";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	HubLinkedDocumentViewModel,
	HubItemMediaPreviewViewModel,
	HubItemViewerListItemModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemLayoutDetailsViewModel,
	HubItemMediaDetailsViewModel
} from "client/Api/Hub/common.details";

export type PreviewItemCategoryModel = {
	id: number;
	name: string;
};

export type PreviewItemUserModel = {
	id: number;
	name: string;
};

export type KnowledgeItemPreviewItemModel = {
	id: number;
	title: string;
	descriptionPreview: string;
	publicationDate: string;
	lastModifiedDate: string;
	media: HubItemMediaPreviewViewModel;
	category: PreviewItemCategoryModel | null;
	author: PreviewItemUserModel | null;
	viewsCount: number;
	pinDate: string | null;
	tags: readonly TagViewModel[];
	relevanceScore: number | null;
	isNew: boolean;
};

export type DashboardCategoryViewModel = {
	id: number;
	name: string;
	amountOfPublishedItems: number;
	topItems: readonly KnowledgeItemPreviewItemModel[];
};

export type DashboardViewModel = {
	categories: readonly DashboardCategoryViewModel[];
};

export function dashboard(signal?: AbortSignal): Promise<DashboardViewModel> {
	return environmentHttpClient.get<DashboardViewModel>(
		"UiApi/Hub/KnowledgeItems/Dashboard",
		undefined,
		{ signal }
	);
}

export const enum KnowledgeItemSortProperty {
	Views = 0,
	PublicationDate = 1,
	Title = 2,
	PinDate = 3,
	Relevance = 4
};

export type KnowledgeItemOrderModel = {
	property: KnowledgeItemSortProperty;
	direction: SortDirection;
};

// We use strings rather then Dates for the fields to promote reusing
// the string which was retrieved from the backend. The reason being
// that we get a 7 precision fractional second string from the backend
// and JS only can handle a 3 precision one. So it will round/truncate
// to 3 precision resulting that it is more likely to get the same item
// back you used to computed this model. Revisit when the Temporal api
// is available in the product.
export type KnowledgeItemPagingInformationModel = {
	id: number;
	publicationDate: string;
	viewsCount: number;
	title: string;
	pinDate: string | null;
	relevanceScore: number | null;
};

export type KnowledgeItemListQueryParameters = {
	categoryId?: number | null;
	authorId?: number | null;
	tagId?: number | null;
	searchTerm?: string | null;
	isPinned?: boolean | null;
	order?: KnowledgeItemOrderModel | null;
	pagingInformation?: KnowledgeItemPagingInformationModel | null;
	amount?: number | null;
	isNew?: boolean | null;
};

export function list(
	parameters: KnowledgeItemListQueryParameters,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<KnowledgeItemPreviewItemModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<KnowledgeItemPreviewItemModel>>(
		"UiApi/Hub/KnowledgeItems",
		parameters,
		{ signal }
	);
}

export type HubItemBreadcrumbItem = {
	id: number;
	name: string;
};

export type HubItemBreadcrumbModel = {
	groups: readonly HubItemBreadcrumbItem[];
	category: HubItemBreadcrumbItem;
};

export type KnowledgeItemDetailsPageViewModel = {
	item: KnowledgeItemDetailsViewModel;
	commentOptions: OptionalModel<HubCommentOptionsModel>;
};

export type KnowledgeItemDetailsViewModel = {
	id: number;
	title: string;
	layout: HubItemLayoutDetailsViewModel;
	media: HubItemMediaDetailsViewModel;
	publicationDate: string;
	modifiedDate: string;
	author: UserIdentityModel | null;
	modifiedBy: UserIdentityModel | null;
	categoryId: number;
	tags: readonly TagViewModel[];
	isEditable: boolean;
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
	pinDate: string | null;
	isNew: boolean;
	breadcrumb: OptionalModel<HubItemBreadcrumbModel | null>;
};

export type KnowledgeItemPinModel = {
	id: number;
	title: string;
	status: KnowledgeItemStatus;
};

export function get(
	id: number,
	shouldIncludeBreadcrumb?: boolean,
	signal?: AbortSignal
): Promise<KnowledgeItemDetailsPageViewModel> {
	return environmentHttpClient.get<KnowledgeItemDetailsPageViewModel>(
		`UiApi/Hub/KnowledgeItems/${id}`,
		{ shouldIncludeBreadcrumb },
		{ signal }
	);
}

export function registerView(
	id: number,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		`UiApi/Hub/KnowledgeItems/${id}/Views`,
		undefined,
		{ signal }
	);
}

export function registerReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.post<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/React`,
		kind,
		{ signal }
	);
}

export function updateReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.put<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/React`,
		kind,
		{ signal }
	);
}

export function unregisterReaction(
	id: number,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.delete<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/React`,
		undefined,
		{ signal }
	);
}

export function getReactionCounts(
	id: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return environmentHttpClient.get<readonly ReactionCounterModel[]>(
		`UiApi/Hub/KnowledgeItems/${id}/ReactionCounts`,
		undefined,
		{ signal }
	);
}

export function getReactions(
	id: number,
	pageSize?: number,
	skip?: number,
	kind?: ReactionKind,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`UiApi/Hub/KnowledgeItems/${id}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}

export function registerFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.post<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export function unregisterFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.delete<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export type KnowledgeItemStatisticsModel = {
	knowledgeItemId: number;
	viewsCount: number;
	commentsCount: OptionalModel<number>;
	reactionCounters: readonly ReactionCounterModel[];
	currentUserReaction: ReactionKind;
	isCurrentUserFollowingComments: boolean;
};

export function getStatistics(
	id: number,
	signal?: AbortSignal
): Promise<KnowledgeItemStatisticsModel> {
	return environmentHttpClient.get<KnowledgeItemStatisticsModel>(
		`UiApi/Hub/KnowledgeItems/${id}/Statistics`,
		undefined,
		{ signal }
	);
}

export function getRelevantItems(
	id: number,
	pagingInformation: KnowledgeItemPagingInformationModel,
	categoryId: number | null,
	authorId: number | null,
	tagId: number | null,
	searchTerm: string | null,
	order: KnowledgeItemOrderModel | null,
	isNew: boolean | null,
	signal?: AbortSignal
): Promise<readonly KnowledgeItemPreviewItemModel[]> {
	return environmentHttpClient.get<readonly KnowledgeItemPreviewItemModel[]>(
		`UiApi/Hub/KnowledgeItems/${id}/Relevant`,
		{
			pagingInformation,
			categoryId,
			authorId,
			tagId,
			searchTerm,
			order,
			isNew
		},
		{ signal }
	);
}

export function listAuthors(signal?: AbortSignal): Promise<readonly UserIdentityModel[]> {
	return environmentHttpClient.get<readonly UserIdentityModel[]>(
		"UiApi/Hub/knowledgeItems/Authors",
		undefined,
		{ signal }
	);
}

export type CategoryGroupOverviewItem = {
	id: number;
	name: string;
	description: string | null;
	imageId: OptionalModel<string>;
};

export type GroupGroupOverviewItem = {
	id: number;
	name: string;
	description: string | null;
	imageId: OptionalModel<string>;
};

export type GroupOverviewResponseModel = {
	categories: readonly CategoryGroupOverviewItem[];
	groups: readonly GroupGroupOverviewItem[];
};

export type GroupOverviewRequestModel = {
	categoryIds: readonly number[];
	groupIds: readonly number[];
};

export function listGroupOverviewData(
	model: GroupOverviewRequestModel,
	signal?: AbortSignal
): Promise<GroupOverviewResponseModel> {
	return environmentHttpClient.post<GroupOverviewResponseModel>(
		`UiApi/Hub/KnowledgeItems/GroupOverview`,
		model,
		{ signal }
	);
}

export type CategoryPageContentModel = {
	content: string | null;
};

export function getCategoryPageContent(
	categoryId: number,
	signal?: AbortSignal
): Promise<CategoryPageContentModel> {
	return environmentHttpClient.get<CategoryPageContentModel>(
		`UiApi/Hub/KnowledgeItems/Category/${categoryId}/Page`,
		{ signal }
	);
}

export function listViewers(
	id: number,
	skip: number,
	take: number,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubItemViewerListItemModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<HubItemViewerListItemModel>>(
		`UiApi/Hub/KnowledgeItems/${id}/Viewers`,
		{
			skip,
			take
		},
		{ signal }
	);
}

export function doesUserHaveAccess(
	id: number,
	userId: number,
	signal?: AbortSignal
): Promise<boolean> {
	return environmentHttpClient.get<boolean>(
		`UiApi/Hub/KnowledgeItems/${id}/DoesUserHaveAccess`,
		{
			userId
		},
		{ signal }
	);
}

export function findUsers(
	id: number,
	searchTerm: string,
	amount: number,
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return environmentHttpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Hub/KnowledgeItems/${id}/Users`,
		{
			searchTerm,
			amount
		},
		{ signal }
	);
}